import { Injectable } from '@angular/core';
import _get from 'lodash-es/get';
import _forEach from 'lodash-es/forEach';
import * as moment from 'moment';
import { ISegmentInformation, ISegments, ISegment } from 'libraries/model/air-quote/IQuote';
import { IGetAirTripQuotesDictionary, ISector } from 'libraries/service/resources/airTrip/IGetAirTripQuotes';
import { ICodeName } from 'libraries/service/resources/model/shared/ICodeName';
import { DateService } from 'libraries/date/date.service';
import { ICarbonFootPrint } from "libraries/component/carbon-footprint/carbon-footprint.interface";

export interface ISectorData {
    parentSegmentDuration?: number;
    parentSegmentXid?: string;
    flight: string;
    aircraft: {
        name: string;
    };
    carrier: ICodeName;
    from: IFromTo;
    to: IFromTo;
    durationStop: string;
    seat: {
        code: string;
        cabin?: string
    };
    begin: string;
    end: string;
    duration: string;
    operatedBy?: {
        carrier: ICodeName;
    };
    carbonFootprint?: ICarbonFootPrint;
}

export interface ISectorDataObject { [key: string]: ISectorData[]; }

interface IFromTo {
    airport: ICodeName;
    city: ICodeName;
    country: ICodeName;
    terminal: {
        code: string;
    };
}


@Injectable()
export class FlightDetailsService {
    constructor(
        private readonly dateService: DateService,
    ) { }

    public buildForDetails(
        segments: ISegments,
        dictionary: IGetAirTripQuotesDictionary,
        journeyId: string[],
        timeFormat: string = "HH[h]mm[min]",
        parentSegmentXid?: string
    ): ISectorDataObject {
        const output: ISectorDataObject = {};

        _forEach(segments, (segment: ISegment, segmentKey: string): void => {
            journeyId.forEach((journey: string): void => {
                if (segment[journey]) {
                    output[segmentKey] = this.getSectorsInformation(segment[journey], dictionary, timeFormat, parentSegmentXid);
                }
            });
        });

        return output;
    }

    private getSectorsInformation(
        segment: ISegmentInformation,
        dictionary: IGetAirTripQuotesDictionary,
        timeFormat: string = "HH[h]mm[min]",
        parentSegmentXid?: string
    ): ISectorData[] {
        return segment.sector.map((sector: ISector): ISectorData => {
            const seatCode: string = Object.keys(sector.seat)[0];
            return {
                parentSegmentDuration: segment.duration?.minutes,
                parentSegmentXid,
                flight: _get(sector, 'flight'),
                aircraft: {
                    name: sector.aircraft && dictionary.aircraft[sector.aircraft] && dictionary.aircraft[sector.aircraft].name,
                },
                carrier: sector.carrier?.code && dictionary?.carrier ?
                    dictionary.carrier[sector.carrier.code] :
                    (sector.carrier ? {code: sector.carrier.code} : null),
                from: getFromTo(dictionary, sector, 'from'),
                to: getFromTo(dictionary, sector, 'to'),
                durationStop: _get(sector, 'durationStop') ? getFormattedDate(_get(sector, 'durationStop'), timeFormat) : null,
                seat: sector.seat ? {
                    code: seatCode,
                    cabin: sector.seat[seatCode].xCabin || null
                }: null,
                begin: _get(sector, 'begin'),
                end: _get(sector, 'end'),
                duration: sector.duration && sector.duration.minutes ?
                    this.dateService.getDuration(sector.duration.minutes, timeFormat) :
                    this.dateService.getDurationFromBeginEnd(sector.begin, sector.end),
                operatedBy: sector.operatedBy ? { carrier: dictionary.carrier[sector.operatedBy.carrier.code] } : null,
                carbonFootprint: sector.carbonFootprint,
            };
        });
    }
}

function getFromTo(dictionary: IGetAirTripQuotesDictionary, sector: ISector, direction: string): IFromTo {
    if(Boolean(dictionary?.airport?.[_get(sector, direction)])){
        return {
            airport: dictionary.airport[_get(sector, direction)].airport,
            city: dictionary.airport[_get(sector, direction)].city,
            country: dictionary.airport[_get(sector, direction)].country,
            terminal: {
                code: sector[`${direction}Terminal`]
            }
        };
    }
    return {
        airport: _get(sector, direction),
        city: _get(sector, direction),
        country: _get(sector, direction),
        terminal: {
            code: sector[`${direction}Terminal`]
        }
    };
}

function getFormattedDate(time: { minutes: number }, timeFormat: string = "HH[h]mm[min]"): string {
    return moment.utc().startOf('day').add(time).format(timeFormat);
}
